import React, { useEffect, useState, type FC } from "react";
import classes from "./NavbarGenres.module.css";
import { motion } from "framer-motion";
import { setMovieGenre } from "../Slices/movieGenreSlice";
import { setSeriesGenre } from "../Slices/seriesGenreSlice";
import { useAppDispatch, useAppSelector } from "../Store/hooks";

type Genres = {
  result: {
    genres: { id: number; name: string }[];
  };
};

type NavbarGenresProps = {
  genres: Genres;
  genreLoading: boolean;
  genreType: "movies" | "series";
};

const NavbarGenres: FC<NavbarGenresProps> = ({
  genreType,
  genres,
  genreLoading,
}) => {
  const [leftConstraint, setLeftConstraint] = useState<number>(0);
  const dispatch = useAppDispatch();

  const { movieGenre, movieGenreId } = useAppSelector(
    (state) => state.movieGenreSlice
  );
  const { seriesGenre, seriesGenreId } = useAppSelector(
    (state) => state.seriesGenreSlice
  );

  useEffect(() => {
    const updateConstraints = () => {
      // logic to implement the drag to upto how much screen
      // padding from screen sides + total cards * width of card + total cards * gap between cards + total cards * box shadow twice

      if (window.innerWidth <= 800) {
        setLeftConstraint(
          10 +
            (genres?.result?.genres.length + 1) * 100 +
            (genres?.result?.genres.length + 1) * 10 +
            (genres?.result?.genres.length + 1) * 1 -
            window.innerWidth
        );
      } else {
        setLeftConstraint(
          window.innerWidth * 0.05 +
            (genres?.result?.genres.length + 1) * 100 +
            (genres?.result?.genres.length + 1) * 10 +
            (genres?.result?.genres.length + 1) * 1 -
            window.innerWidth
        );
      }
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => {
      window.removeEventListener("resize", updateConstraints);
    };
  }, [genres]);

  const genreClickHandler: (genreData: {
    name: string;
    id: number | null;
  }) => void = (genreData) => {
    if (genreType === "movies") {
      dispatch(setMovieGenre({ movie: genreData?.name, id: genreData?.id }));
    } else {
      dispatch(setSeriesGenre({ series: genreData?.name, id: genreData?.id }));
    }
  };

  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setStartPosition({ x: event.clientX, y: event.clientY });
    setIsDragging(false);
  };

  const handleMouseUp = (
    event: React.MouseEvent<HTMLButtonElement>,
    data: { name: string; id: number | null }
  ) => {
    const distance = Math.sqrt(
      Math.pow(event.clientX - startPosition.x, 2) +
        Math.pow(event.clientY - startPosition.y, 2)
    );
    if (distance < 5 && !isDragging) {
      genreClickHandler(data);
    }
  };

  const handleMouseMove = () => {
    setIsDragging(true);
  };

  return (
    <div className={classes.navbar_genres}>
      {genreLoading ? (
        <motion.div
          drag={"x"}
          dragConstraints={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          transition={{
            type: "spring",
            damping: 100,
            stiffness: 100,
          }}
          className={classes.genre_grid}
          style={{
            gridTemplateColumns: `repeat(${15 + 1},100px)`,
          }}
        >
          {Array.from({ length: 16 })?.map((genre, idx) => (
            <motion.div
              className={classes.genreLoading}
              key={idx}
              initial={{
                background:
                  "linear-gradient(45deg,hsl(0deg 0% 14%) 0%,hsl(0deg 0% 14%) 20%,hsl(0deg 0% 14%) 29%,hsl(0deg 0% 14%) 36%,hsl(0deg 0% 14%) 43%,hsl(0deg 0% 14%) 50%,hsl(228deg 3% 12%) 57%,hsl(228deg 5% 11%) 64%,hsl(228deg 8% 9%) 71%,hsl(228deg 11% 8%) 80%,hsl(228deg 14% 7%) 100%)",
              }}
              animate={{
                background: [
                  "linear-gradient(45deg, hsl(228deg 14% 7%) 0%, hsl(228deg 11% 8%) 20%, hsl(228deg 8% 9%) 29%, hsl(228deg 5% 11%) 36%, hsl(228deg 3% 12%) 43%, hsl(0deg 0% 14%) 50%, hsl(228deg 3% 12%) 57%, hsl(228deg 5% 11%) 64%, hsl(228deg 8% 9%) 71%, hsl(228deg 11% 8%) 80%, hsl(228deg 14% 7%) 100%)",
                  "linear-gradient(45deg,hsl(0deg 0% 14%) 0%,hsl(0deg 0% 14%) 20%,hsl(0deg 0% 14%) 29%,hsl(0deg 0% 14%) 36%,hsl(0deg 0% 14%) 43%,hsl(0deg 0% 14%) 50%,hsl(228deg 3% 12%) 57%,hsl(228deg 5% 11%) 64%,hsl(228deg 8% 9%) 71%,hsl(228deg 11% 8%) 80%,hsl(228deg 14% 7%) 100%)",
                  "linear-gradient(45deg,hsl(228deg 14% 7%) 0%,hsl(228deg 11% 8%) 20%,hsl(228deg 8% 9%) 29%,hsl(228deg 5% 11%) 36%,hsl(228deg 3% 12%) 43%,hsl(0deg 0% 14%) 50%,hsl(0deg 0% 14%) 57%,hsl(0deg 0% 14%) 64%,hsl(0deg 0% 14%) 71%,hsl(0deg 0% 14%) 80%,hsl(0deg 0% 14%) 100%)",
                ],
              }}
              transition={{
                duration: 2,
                repeatType: "mirror",
                repeat: Infinity,
                ease: "linear",
              }}
            ></motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          drag={"x"}
          dragConstraints={{
            top: 0,
            left: -leftConstraint,
            right: 0,
            bottom: 0,
          }}
          transition={{
            type: "spring",
            damping: 100,
            stiffness: 100,
          }}
          className={classes.genre_grid}
          style={{
            gridTemplateColumns: `repeat(${
              genres?.result?.genres.length + 1
            },100px)`,
          }}
        >
          <button
            type="button"
            className={`${classes.genre} 
            ${
              (genreType === "movies" && movieGenreId === null) ||
              (genreType === "series" && seriesGenreId === null)
                ? classes.genre_active
                : null
            }`}
            // onClick={() => genreClickHandler({ name: "all", id: null })}
            onMouseDown={(event)=>handleMouseDown(event)}
            onMouseUp={(event) =>
              handleMouseUp(event, { name: "all", id: null })
            }
            onMouseMove={handleMouseMove}
          >
            All
          </button>
          {genres?.result?.genres.map((genre, idx) => (
            <button
              type="button"
              className={`${classes.genre} ${
                (genreType === "movies" &&
                  movieGenreId === genre.id &&
                  classes.genre_active) ||
                (genreType === "series" &&
                  seriesGenreId === genre.id &&
                  classes.genre_active)
              }`}
              key={idx}
              // onClick={() => genreClickHandler(genre)}
              onMouseDown={(event)=>handleMouseDown(event)}
              onMouseUp={(event) =>
                handleMouseUp(event, genre)
              }
              onMouseMove={handleMouseMove}
            >
              {genre.name}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default NavbarGenres;
